import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, ArrowRight, KeyRound, UserPlus, SlidersHorizontal } from 'lucide-react';
import api from '../utils/api';

const HERO_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/3e238895-91ed-4407-8b1b-de9f9656b7ae/images/311418e67ffa9c0b293e00817758e3c923d2a59357e137bc020611d642767bde.png';

const BRAND_STRIP = ['Amiri', 'Hugo Boss', 'Karl Lagerfeld', 'Monastery', 'Casablanca', 'Dolce & Gabbana', 'Armani Exchange', 'Off-White', 'Essentials', 'Represent'];

const Home = () => {
  const [config, setConfig] = useState(null);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [configRes, featuredRes, categoriesRes] = await Promise.all([
          api.get('/config'),
          api.get('/products/home-featured'),
          api.get('/categories')
        ]);
        setConfig(configRes.data);
        setFeaturedProducts(featuredRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const heroBackground = config?.hero_backgrounds?.[0] || 'https://images.pexels.com/photos/707667/pexels-photo-707667.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

  const handleCategoryClick = (categoryName) => {
    navigate(`/catalog?category=${encodeURIComponent(categoryName)}`);
  };

  return (
    <div>
      {/* ============ HERO SECTION — Wholesale B2B ============ */}
      <section className="relative min-h-screen overflow-hidden bg-[#050505]" data-testid="hero-section">
        {/* Background editorial image */}
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMAGE}
            alt="HAUTE Premium Wholesale Fashion"
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          {/* Dark gradient overlays for legibility */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-[#050505]" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-black/40" />
        </div>

        {/* Content */}
        <div className="relative z-10 min-h-screen flex flex-col">
          <div className="flex-1 flex items-center px-4 md:px-12 lg:px-24 pt-12 pb-24 md:pb-28">
            <div className="max-w-3xl">
              <span
                data-testid="hero-overline"
                className="inline-block mb-6 text-[10px] sm:text-xs font-semibold tracking-[0.35em] uppercase text-white/70 border border-white/20 px-3 py-1.5 backdrop-blur-sm"
              >
                Distribuidor Mayorista de Moda Premium
              </span>

              <h1
                data-testid="hero-title"
                className="font-['Playfair_Display'] text-[64px] sm:text-[96px] lg:text-[128px] font-bold tracking-[-0.04em] leading-[0.9] mb-8 animate-fade-in-up"
              >
                HAUTE
              </h1>

              <p
                data-testid="hero-subtitle"
                className="text-base md:text-lg lg:text-xl text-white/75 mb-10 font-light tracking-wide max-w-xl leading-relaxed animate-fade-in-up"
                style={{ animationDelay: '0.15s' }}
              >
                Marcas exclusivas para boutiques, distribuidores y emprendedores.
              </p>

              <div className="flex flex-wrap gap-3 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                <Link
                  to="/catalog"
                  data-testid="hero-catalog-btn"
                  className="group inline-flex items-center justify-center gap-3 bg-white text-black hover:bg-gray-200 transition-all duration-300 px-6 py-4 md:px-7 md:py-5 uppercase tracking-[0.18em] text-[11px] md:text-xs font-semibold"
                >
                  Ver Catálogo Mayorista
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/portal/login"
                  data-testid="hero-login-btn"
                  className="inline-flex items-center justify-center gap-2.5 bg-white/10 hover:bg-white/20 border border-white/30 hover:border-white/60 text-white transition-all duration-300 px-6 py-4 md:px-7 md:py-5 uppercase tracking-[0.18em] text-[11px] md:text-xs font-semibold backdrop-blur-sm"
                >
                  <KeyRound className="w-3.5 h-3.5" />
                  Ingresar
                </Link>
                <Link
                  to="/portal/register"
                  data-testid="hero-access-btn"
                  className="inline-flex items-center justify-center gap-3 border border-white/30 text-white hover:bg-white/10 hover:border-white transition-all duration-300 px-6 py-4 md:px-7 md:py-5 uppercase tracking-[0.18em] text-[11px] md:text-xs font-semibold backdrop-blur-sm"
                >
                  Solicitar Acceso
                </Link>
              </div>
            </div>
          </div>

          {/* Brand marquee strip */}
          <div className="absolute bottom-0 left-0 right-0 bg-[#050505]/85 backdrop-blur-xl border-t border-white/10 overflow-hidden py-5" data-testid="brand-marquee">
            <div className="animate-marquee">
              {[...Array(2)].map((_, dupIdx) => (
                <div key={dupIdx} className="flex gap-12 md:gap-16 items-center whitespace-nowrap px-6 md:px-8">
                  {BRAND_STRIP.map((brand) => (
                    <span
                      key={`${dupIdx}-${brand}`}
                      className="font-['Playfair_Display'] text-base md:text-xl text-white/40 hover:text-white/70 transition-colors uppercase tracking-[0.15em]"
                    >
                      {brand}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Portal Clientes - Acceso & Registro */}
      <section className="px-4 md:px-12 lg:px-24 py-16 md:py-24 border-t border-white/5" data-testid="portal-section">
        <div className="text-center mb-10 md:mb-14">
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-3">Cuenta personal</p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl tracking-tighter mb-3">
            Portal de Clientes
          </h2>
          <p className="text-sm text-white/60 max-w-xl mx-auto">
            Accede a tu historial de facturas, estado de cuenta y crea pre-pedidos personalizados.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
          {/* Login con código */}
          <Link
            to="/portal/login"
            data-testid="home-portal-login-card"
            className="group bg-gradient-to-br from-[#0F0F0F] to-[#0A0A0A] border border-white/10 hover:border-white/30 p-8 md:p-10 transition-all duration-500 hover:-translate-y-1"
          >
            <div className="w-14 h-14 rounded-full bg-white/5 border border-white/10 group-hover:border-white/30 flex items-center justify-center mb-6 transition-colors">
              <KeyRound className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl mb-3">Ya soy cliente</h3>
            <p className="text-sm text-white/60 mb-6 leading-relaxed">
              Ingresa con tu <strong className="text-white">código personal</strong> (1 letra + 4 dígitos) para ver tus facturas, saldo y pre-pedidos.
            </p>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white group-hover:gap-3 transition-all">
              Acceder con código
              <ChevronRight className="w-4 h-4" />
            </span>
          </Link>

          {/* Registro */}
          <Link
            to="/portal/register"
            data-testid="home-portal-register-card"
            className="group bg-gradient-to-br from-[#0F0F0F] to-[#0A0A0A] border border-white/10 hover:border-white/30 p-8 md:p-10 transition-all duration-500 hover:-translate-y-1"
          >
            <div className="w-14 h-14 rounded-full bg-white/5 border border-white/10 group-hover:border-white/30 flex items-center justify-center mb-6 transition-colors">
              <UserPlus className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl mb-3">Nuevo cliente</h3>
            <p className="text-sm text-white/60 mb-6 leading-relaxed">
              Regístrate para abrir tu cuenta. Recibirás tu <strong className="text-white">código de acceso</strong> personal por WhatsApp.
            </p>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white group-hover:gap-3 transition-all">
              Crear cuenta
              <ChevronRight className="w-4 h-4" />
            </span>
          </Link>
        </div>
      </section>

      {/* Featured Products by Category */}
      {featuredProducts.length > 0 && (
        <section className="px-4 md:px-12 lg:px-24 py-24 md:py-40" data-testid="featured-products-section">
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-3">Colección Exclusiva</p>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl tracking-tighter mb-4">
                Productos Destacados
              </h2>
              <p className="text-sm text-white/60 uppercase tracking-[0.2em]">
                Un producto seleccionado por cada categoría
              </p>
            </div>
            <Link
              to="/catalog"
              data-testid="filters-link"
              className="mt-6 md:mt-0 inline-flex items-center gap-2 text-white/60 hover:text-white text-xs uppercase tracking-[0.2em] transition-colors"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Ver con Filtros
            </Link>
          </div>

          {/* Premium Category Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
            {featuredProducts.map((product) => {
              const categoryDisplay = product.category_display || product.category;
              
              return (
                <article 
                  key={product.id} 
                  data-testid={`featured-category-${product.category}`}
                  className="group relative bg-gradient-to-b from-[#0D0D0D] to-[#080808] border border-white/[0.06] hover:border-white/[0.12] transition-all duration-500"
                >
                  {/* Category Badge - Top Left */}
                  <div className="absolute top-0 left-0 z-10">
                    <button 
                      onClick={() => handleCategoryClick(product.category)}
                      className="bg-white/[0.03] backdrop-blur-sm border-r border-b border-white/[0.08] px-4 py-2.5 text-[10px] uppercase tracking-[0.25em] text-white/70 hover:text-white hover:bg-white/[0.08] transition-all duration-300"
                    >
                      {categoryDisplay}
                    </button>
                  </div>
                  
                  {/* Image Container - Fixed Aspect Ratio - Clicking navigates to category */}
                  <button 
                    onClick={() => handleCategoryClick(product.category)}
                    className="block relative aspect-[3/4] overflow-hidden bg-gradient-to-b from-[#111111] to-[#0A0A0A] w-full cursor-pointer"
                    data-testid={`category-image-${product.category}`}
                  >
                    {/* Image with elegant containment */}
                    <div className="absolute inset-0 flex items-center justify-center p-4 md:p-6">
                      <img 
                        src={product.images[0]} 
                        alt={product.name}
                        className="max-w-[90%] max-h-[90%] w-auto h-auto object-contain drop-shadow-2xl transition-all duration-700 group-hover:scale-105"
                        style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.4))' }}
                      />
                    </div>
                    
                    {/* Subtle vignette effect */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20 pointer-events-none" />
                    
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-500 flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-400 bg-white text-black px-8 py-3.5 text-[11px] uppercase tracking-[0.2em] font-semibold shadow-2xl">
                        Ver Categoría
                      </span>
                    </div>
                  </button>
                  
                  {/* Product Info Container - Fixed Height */}
                  <div className="p-5 border-t border-white/[0.06]">
                    {/* Brand */}
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                      {product.brand}
                    </p>
                    
                    {/* Category Name - Fixed 2 lines - Clicking navigates to category */}
                    <button 
                      onClick={() => handleCategoryClick(product.category)} 
                      className="block text-left w-full"
                    >
                      <h3 className="text-sm font-medium text-white/90 group-hover:text-white transition-colors leading-snug h-[2.6rem] line-clamp-2">
                        {categoryDisplay}
                      </h3>
                    </button>
                    
                    {/* Price and Action Row */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/[0.06]">
                      <div>
                        {config?.show_prices !== false ? (
                          product.promotional_price ? (
                            <div className="flex items-center gap-2">
                              <span className="text-base font-semibold text-white">
                                ${product.promotional_price.toLocaleString()}
                              </span>
                              <span className="text-xs text-white/40 line-through">
                                ${product.price.toLocaleString()}
                              </span>
                            </div>
                          ) : (
                            <span className="text-base font-semibold text-white">
                              ${product.price.toLocaleString()}
                            </span>
                          )
                        ) : (
                          <span className="text-xs text-white/40 italic">Consultar precio</span>
                        )}
                      </div>
                      
                      {/* View Category Arrow */}
                      <button 
                        onClick={() => handleCategoryClick(product.category)}
                        className="w-10 h-10 flex items-center justify-center border border-white/10 hover:border-white/30 hover:bg-white/5 transition-all duration-300 group/btn"
                      >
                        <ChevronRight className="w-4 h-4 text-white/40 group-hover/btn:text-white transition-colors" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Decorative Corner Accents */}
                  <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-white/[0.08] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-white/[0.08] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </article>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="mt-20 flex flex-col sm:flex-row items-center justify-center gap-6">
            <Link
              to="/catalog"
              data-testid="view-all-products-button"
              className="inline-flex items-center gap-3 bg-white text-black hover:bg-gray-200 transition-colors duration-300 rounded-none px-10 py-5 uppercase tracking-widest text-xs font-semibold"
            >
              Ver Todos los Productos
              <ChevronRight className="w-4 h-4" />
            </Link>
            <Link
              to="/catalog"
              data-testid="filters-button-bottom"
              className="inline-flex items-center gap-3 border border-white/20 text-white hover:border-white hover:bg-white/5 transition-colors duration-300 rounded-none px-10 py-5 uppercase tracking-widest text-xs font-semibold"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filtrar Productos
            </Link>
          </div>
        </section>
      )}

      {/* Experience Section removed by user request */}
    </div>
  );
};

export default Home;
